import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, LinkExternal01, Metric, Spinner } from "@opendate-app/setlist";

import { useMarketingSettingsContext } from "../MarketingSettingsIndexContainer";
import PhoneNumberManager from "./PhoneNumberManager";
import { toastError } from "../../../../shared/toastHelper";

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: "24px 0",
  },
  metrics: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
  },
  billingRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  muted: {
    fontSize: 12,
    color: "var(--sl-text-tertiary)",
  },
};

const TwilioSubaccount = () => {
  const { team, venueOwnership, csrfToken } = useMarketingSettingsContext();
  const smsStatus = venueOwnership?.sms_status;

  const [billingData, setBillingData] = useState(null);
  const [loading, setLoading] = useState(true);

  const initialPhoneNumber = smsStatus?.messaging_services?.[0]?.twilio_phone_numbers?.[0]?.phone_number;

  useEffect(() => {
    fetchBillingData();
  }, [team.id]);

  const fetchBillingData = async () => {
    setLoading(true);
    try {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      const response = await axios.get(`/teams/${team.id}/sms_subscription`);
      setBillingData(response.data);
    } catch (error) {
      toastError("Failed to fetch billing data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return "—";
    return new Date(isoDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const usage = billingData?.usage || { total_cents: 0, message_count: 0 };
  const billing = billingData?.billing;

  const openBillingPortal = async () => {
    try {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      const { data } = await axios.post(`/teams/${team.id}/sms_subscription/billing_portal`);
      window.open(data.url, "_blank");
    } catch (error) {
      toastError("Failed to open billing portal");
    }
  };

  return (
    <div style={styles.container}>
      <PhoneNumberManager
        key={venueOwnership.id}
        teamId={team.id}
        venueOwnershipId={venueOwnership.id}
        initialPhoneNumber={initialPhoneNumber}
        csrfToken={csrfToken}
      />

      {loading ? (
        <div style={styles.loading}>
          <Spinner aria-label="Loading billing" />
        </div>
      ) : (
        <>
          <div style={styles.metrics}>
            <Metric label="Messages Sent" value={usage.message_count.toLocaleString()} />
            <Metric label="Current Usage" value={formatCurrency(usage.total_cents)} />
            <Metric label="Next Billing Date" value={formatDate(billing?.next_billing_date)} />
          </div>

          <div style={styles.billingRow}>
            <div style={styles.muted}>
              $0.012 per message · Billed monthly or when usage exceeds $900
            </div>
            <Button trailingIcon={LinkExternal01} onClick={openBillingPortal}>Manage Billing</Button>
          </div>
        </>
      )}
    </div>
  );
};

export default TwilioSubaccount;
